import React, {useRef, useMemo} from 'react'
import {Box, Flex, Text, Icon, Link as ChakraLink, useBreakpointValue} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {Link as RouterLink} from 'react-router-dom'
import {ChevronDownIcon, Globe} from '../icons'
import CountrySelector from '../country-selector'
import {useGlobalHeader} from '../../contexts/global-elements-context'
import {useGetLinkData} from '../../contexts/category-url-context'

const TopBar = ({showCountrySelectorOnFooter}) => {
    const isMobile = useBreakpointValue({base: true, md: false})
    const isDesktop = useBreakpointValue({base: false, md: true})
    const countrySelectorRef = useRef(null)
    // Get header content from global elements context (grg-web-uk-global)
    const {data: headerData} = useGlobalHeader()
    const headerBanner = headerData?.banner
    // Check if we have CMS content
    const isFromCMS = !!headerBanner?.message

    // Resolve CTA link using the same pattern as other CMS components
    const getLinkData = useGetLinkData()
    const ctaLinkData = useMemo(() => {
        if (!headerBanner?.cta?.link) return null
        return getLinkData(headerBanner.cta.link)
    }, [headerBanner?.cta?.link])

    const isExternalUrl = ctaLinkData?.isExternalUrl || false
    const LinkComponent = isExternalUrl ? ChakraLink : RouterLink

    return (
        <Box bg="black" color="white" w="100%" minH={{base: '34px', lg: '32px'}}>
            <Flex
                justify={{base: 'space-between', lg: 'center'}}
                align="center"
                maxW="container.xxxl"
                height="100%"
                mx="auto"
                position="relative"
                px={4}
                py={{base: '5px', lg: 0}}
            >
                <Text
                    textAlign={{base: 'start', lg: 'center'}}
                    flex={{base: '1', lg: 'none'}}
                    position={{base: 'relative'}}
                    left={{base: 'auto', lg: '50%'}}
                    transform={{base: 'none', lg: 'translateX(-50%)'}}
                    textTransform="uppercase"
                    variant="promoBarText"
                    paddingBottom={{lg: '2px'}}
                >
                    {isFromCMS ? (
                        ctaLinkData?.generatedURL ? (
                            <Box
                                as={LinkComponent}
                                {...(isExternalUrl
                                    ? {href: ctaLinkData.generatedURL}
                                    : {to: ctaLinkData.generatedURL}
                                )}
                                {...(!ctaLinkData?.stayOnSamePage && ctaLinkData?.target && {
                                    target: ctaLinkData.target,
                                    rel: ctaLinkData.rel
                                })}
                                data-testid="top-bar-cta"
                            >
                                {headerBanner.message}{' '}
                                {headerBanner.cta?.text && (
                                    <Text as="span" textDecoration="underline">
                                        {headerBanner.cta.text}
                                    </Text>
                                )}
                            </Box>
                        ) : (
                            <>
                                {headerBanner.message}{' '}
                                {headerBanner.cta?.text && (
                                    <Text as="span" textDecoration="underline">
                                        {headerBanner.cta.text}
                                    </Text>
                                )}
                            </>
                        )
                    ) : (
                        <FormattedMessage
                            defaultMessage="New in Essentials"
                            id="top_bar.text"
                        />
                    )}
                </Text>

                {isMobile && !showCountrySelectorOnFooter &&
                    <Flex
                        align="center"
                        ml="auto"
                        _hover={{cursor: 'pointer'}}
                        columnGap={1}
                        onClick={() => {
                            if (isMobile && countrySelectorRef.current?.onToggle) {
                                countrySelectorRef.current.onToggle()
                            }
                        }}
                        onKeyDown={(e) => {
                            e.preventDefault()
                            if (isMobile && e.key === 'Enter' && countrySelectorRef.current?.onToggle) {
                                countrySelectorRef.current.onToggle()
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Country selector"
                    >
                        <Icon as={Globe} boxSize={4} pointerEvents="none" />
                        <CountrySelector triggerRef={countrySelectorRef} />
                        <Icon as={ChevronDownIcon} boxSize={5} pointerEvents="none" />
                    </Flex>
                }

                {isDesktop && !showCountrySelectorOnFooter &&
                    <Flex
                        align="center"
                        ml="auto"
                        _hover={{cursor: 'pointer'}}
                        columnGap={1}
                    >
                        <CountrySelector triggerRef={countrySelectorRef} />
                    </Flex>
                }
            </Flex>
        </Box>
    )
}

export default TopBar
